import React, { useState } from "react";
import axios from "axios";
import { useT } from "../i18n/LanguageContext";
import { SectionTag } from "../components/Section";
import { MapPin, Phone, Mail, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFFICES = [
  {
    country: "DR Congo",
    city: "Kinshasa",
    address: "Av. Col Mondjiba 2366, C/Gombe, Kinshasa",
    phones: ["+243 81 558 6725", "+243 81 558 6730", "+243 81 558 6731", "+243 81 558 6732", "+243 81 558 6733"],
  },
];

const empty = { name: "", email: "", company: "", country: "", phone: "", subject: "", message: "" };

export default function ContactPage() {
  const { t, locale } = useT();
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/contact`, { ...form, locale });
      setStatus({ state: "success", message: t("contact.form.success") });
      setForm(empty);
    } catch (err) {
      const msg = err?.response?.data?.detail || t("contact.form.error");
      setStatus({ state: "error", message: typeof msg === "string" ? msg : t("contact.form.error") });
    }
  };

  const inputClass =
    "w-full border border-white/15 bg-[#0A0A0A] px-4 py-3 text-white placeholder-white/30 transition-colors focus:border-brand-red focus:outline-none focus:ring-2 focus:ring-brand-red/40";

  return (
    <main className="pt-32 md:pt-40" data-testid="page-contact">
      <section className="border-b border-white/10 py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
          <SectionTag>{t("contact.tag")}</SectionTag>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl font-medium tracking-tighter text-white sm:text-5xl lg:text-6xl">
            {t("contact.title")}
          </h1>
          <p className="mt-6 max-w-2xl text-base text-brand-textDim">{t("contact.sub")}</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-6 md:px-12 lg:grid-cols-12 lg:px-16">
          {/* Form */}
          <form
            onSubmit={submit}
            className="border border-white/10 bg-[#0A0A0A] p-6 sm:p-10 lg:col-span-7"
            data-testid="contact-form"
            noValidate
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t("contact.form.name")} required>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  className={inputClass}
                  data-testid="contact-input-name"
                  autoComplete="name"
                />
              </Field>
              <Field label={t("contact.form.email")} required>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  className={inputClass}
                  data-testid="contact-input-email"
                  autoComplete="email"
                />
              </Field>
              <Field label={t("contact.form.company")}>
                <input
                  type="text"
                  value={form.company}
                  onChange={update("company")}
                  className={inputClass}
                  data-testid="contact-input-company"
                  autoComplete="organization"
                />
              </Field>
              <Field label={t("contact.form.country")}>
                <input
                  type="text"
                  value={form.country}
                  onChange={update("country")}
                  className={inputClass}
                  data-testid="contact-input-country"
                  autoComplete="country-name"
                />
              </Field>
              <Field label={t("contact.form.phone")}>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  className={inputClass}
                  data-testid="contact-input-phone"
                  autoComplete="tel"
                />
              </Field>
              <Field label={t("contact.form.subject")} required>
                <input
                  type="text"
                  required
                  value={form.subject}
                  onChange={update("subject")}
                  className={inputClass}
                  data-testid="contact-input-subject"
                />
              </Field>
            </div>
            <div className="mt-5">
              <Field label={t("contact.form.message")} required>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  className={`${inputClass} resize-none`}
                  data-testid="contact-input-message"
                />
              </Field>
            </div>

            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="submit"
                disabled={status.state === "loading"}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 bg-brand-red px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-brand-redHover disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status.state === "loading" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("contact.form.sending")}
                  </>
                ) : (
                  t("contact.form.submit")
                )}
              </button>
              {status.state === "success" && (
                <div className="flex items-center gap-2 text-sm text-emerald-400" data-testid="contact-success">
                  <CheckCircle2 className="h-4 w-4" /> {status.message}
                </div>
              )}
              {status.state === "error" && (
                <div className="flex items-center gap-2 text-sm text-brand-red" data-testid="contact-error">
                  <AlertTriangle className="h-4 w-4" /> {status.message}
                </div>
              )}
            </div>
          </form>

          {/* Side info */}
          <aside className="space-y-8 lg:col-span-5">
            <div>
              <p className="font-mono-tag">{t("contact.email_title")}</p>
              <a
                href="mailto:info@bigradapgroup.com"
                className="mt-3 flex items-center gap-2 text-lg text-white hover:text-brand-red"
                data-testid="contact-email-link"
              >
                <Mail className="h-4 w-4" /> info@bigradapgroup.com
              </a>
            </div>

            <div className="space-y-6">
              <p className="font-mono-tag">{t("contact.offices_title")}</p>
              {OFFICES.map((o) => (
                <div key={o.country} className="border border-white/10 bg-[#0A0A0A] p-6">
                  <div className="font-heading text-xl text-white">{o.country}</div>
                  <div className="mt-3 flex items-start gap-2 text-sm text-brand-textDim">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{o.address}</span>
                  </div>
                  <div className="mt-3 space-y-1">
                    {o.phones.map((p) => (
                      <div key={p} className="flex items-center gap-2 text-sm text-brand-textDim">
                        <Phone className="h-4 w-4" /> {p}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-brand-textDim">
        {label} {required && <span className="text-brand-red">*</span>}
      </span>
      <span className="mt-2 block">{children}</span>
    </label>
  );
}
